'use client'

import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Label } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { debounce } from 'lodash'
import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { fetchPatienten } from '../apiCalls/usersApi'

const SelectPatienten = ({ selected, setSelected, text = "Zoek op patient", variant }) => {
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { patienten } = useSelector(state => state.user);
  const [data, setData] = useState(patienten);

  const debouncedSearch = useCallback(
    debounce((searchQuery) => {
      fetchPatienten(50, 0, searchQuery, setLoading, setError, setData);
    }, 300),
    []
  );

  const isWa = variant === 'wa'

  const labelCls = isWa
    ? 'block text-sm font-medium text-[#8696a0]'
    : 'block text-sm/6 font-medium text-gray-900 dark:text-white'

  const inputCls = isWa
    ? 'block w-full rounded-lg border border-[#2a3942] bg-[#2a3942] py-2 pl-3 pr-10 text-sm text-[#e9edef] outline-none placeholder:text-[#8696a0] focus:border-[#00a884] focus:ring-1 focus:ring-[#00a884]'
    : 'block w-full rounded-md bg-white py-1.5 pl-3 pr-12 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 dark:bg-gray-800 dark:text-white'

  const optionsCls = isWa
    ? 'absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-[#2a3942] bg-[#202c33] py-1 text-sm shadow-xl ring-1 ring-black/20 focus:outline-none'
    : 'absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm dark:bg-gray-800 dark:text-white'

  const optionCls = isWa
    ? 'group relative cursor-default select-none py-2 pl-3 pr-9 text-[#e9edef] data-[focus]:bg-[#2a3942] data-[focus]:outline-none'
    : 'group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-indigo-600 data-[focus]:text-white data-[focus]:outline-none'

  const chevronCls = isWa ? 'size-5 text-[#8696a0]' : 'size-5 text-gray-400'

  return (
    <Combobox
      as="div"
      value={selected || data[0]}
      onChange={(person) => {
        setQuery('')
        setSelected(person)
      }}
    >
      {text && <Label className={labelCls}>{text}</Label>}
      <div className="relative mt-2">
        <ComboboxInput
          className={inputCls}
          onChange={(event) => {
            setQuery(event.target.value);
            debouncedSearch(event.target.value);
          }}
          onBlur={() => setQuery('')}
          displayValue={(person) => person?.full_name}
        />
        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
          <ChevronUpDownIcon className={chevronCls} aria-hidden="true" />
        </ComboboxButton>

        {data.length > 0 && (
          <ComboboxOptions className={optionsCls}>
            {data.map((person) => (
              <ComboboxOption
                key={person.id}
                value={person}
                className={optionCls}
              >
                <div className={`flex items-center ${isWa ? 'text-[#e9edef]' : 'dark:text-white'}`}>
                  <img src={person?.avatar} alt="" className="size-6 shrink-0 rounded-full" />
                  <span className="ml-3 truncate group-data-[selected]:font-semibold">{person?.full_name}</span>
                </div>

                <span className={`absolute inset-y-0 right-0 hidden items-center pr-4 group-data-[selected]:flex ${isWa ? 'text-[#00a884] group-data-[focus]:text-[#00a884]' : 'text-indigo-600 group-data-[focus]:text-white'}`}>
                  <CheckIcon className="size-5" aria-hidden="true" />
                </span>
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  )
}

export default SelectPatienten;
